import React, { useState, useRef, useEffect, useMemo, useCallback, forwardRef } from 'react';
import {
  Popover, PopoverTrigger, PopoverContent, PopoverBody,
  Tag, Flex, Box, Text, Input, useColorMode,
} from '@chakra-ui/react';
import { SearchIcon } from '@chakra-ui/icons';
import { useTranslation } from 'react-i18next';
import i18n from '../../i18n';
import { DEFAULT_CATEGORY_COLOR } from '../categories/CategoriesPage';
import { categoryTint } from '../../utils/categoryTint';

/**
 * Get category display text with optional icon
 */
export const getCategoryDisplay = (movement) => {
  if (movement.type === "Transfer") {
    return null;
  }
  const icon = movement.category?.icon || '';
  const name = movement.category?.name || i18n.t('movements:fallback.uncategorized');
  return icon ? `${icon} ${name}` : name;
};

/**
 * Get custom color props for category tag
 */
export const getCategoryTagProps = (movement) => {
  if (!movement.category) {
    return { bg: "redesign.bg.card", color: "redesign.text.muted" };
  }
  const color = movement.category.color || DEFAULT_CATEGORY_COLOR;
  return { bg: `${color}20`, color };
};

/**
 * A movement's category as a pill tinted in the category's own hue, its name
 * in a shade of that hue that reads on the tint; an uncategorized movement
 * gets a dashed outline. Forwards its ref so it can open a popover.
 */
export const CategoryPill = forwardRef(function CategoryPill({ movement, iconOnly = false, maxW = 'none', ...props }, ref) {
  const { colorMode } = useColorMode();
  const tint = movement.category
    ? categoryTint(movement.category.color || DEFAULT_CATEGORY_COLOR, colorMode)
    : null;
  return (
    <Flex
      ref={ref}
      align="center"
      gap="6px"
      h="22px"
      px="8px"
      borderRadius="full"
      border="1px"
      borderStyle={tint ? 'solid' : 'dashed'}
      borderColor={tint ? 'transparent' : 'redesign.border.strong'}
      bg={tint ? tint.bg : 'transparent'}
      color={tint ? tint.fg : 'redesign.text.muted'}
      fontSize="sm"
      fontWeight="500"
      whiteSpace="nowrap"
      maxW={maxW}
      minW={0}
      flexShrink={0}
      {...props}
    >
      {/* The icon takes its own slot: a space after an emoji all but
          vanishes at this size, so the pill's gap does the spacing. */}
      {movement.category?.icon ? (
        <Box as="span" flexShrink={0}>{movement.category.icon}</Box>
      ) : (
        <Box w="6px" h="6px" borderRadius="2px" flexShrink={0} bg={tint ? tint.dot : 'redesign.text.faint'} />
      )}
      {!(iconOnly && movement.category?.icon) && (
        <Text as="span" fontSize="inherit" isTruncated>
          {movement.category?.icon && getCategoryDisplay(movement) ? movement.category.name : getCategoryDisplay(movement)}
        </Text>
      )}
    </Flex>
  );
});

/**
 * Inline category picker - click the badge to change category without opening detail panel
 */
/**
 * `maxW` caps the chip so it can sit in a fixed column without widening it,
 * and `iconOnly` drops the name for the icon when the row has run out of
 * width for both (MI-336). Both default to what every existing caller had.
 *
 * The chip is a tinted pill in the category's own hue, its name in sentence
 * case; an uncategorized movement gets a dashed outline. `variant="tag"`
 * keeps the older uppercase tag.
 */
const InlineCategoryPicker = ({ movement, categories, onCategoryChange, size = "2xs", maxW = "none", iconOnly = false, variant = "pill" }) => {
  const { t } = useTranslation('movements');
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const popoverRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (e) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target)) {
        e.stopPropagation();
        setIsOpen(false);
        setSearch('');
      }
    };
    document.addEventListener('click', handleClickOutside, true);
    return () => document.removeEventListener('click', handleClickOutside, true);
  }, [isOpen]);

  const filteredCategories = useMemo(() =>
    categories
      .filter(c => c.type === movement.type)
      .filter(c => !search || c.name.toLowerCase().includes(search.toLowerCase())),
    [categories, movement.type, search]
  );

  const handleSelect = useCallback((category) => {
    setIsOpen(false);
    setSearch('');
    onCategoryChange(movement.id, category);
  }, [movement.id, onCategoryChange]);

  const close = useCallback(() => { setIsOpen(false); setSearch(''); }, []);

  if (movement.type === "Transfer") return null;

  const toggle = (e) => { e.stopPropagation(); setIsOpen(!isOpen); };

  return (
    <Popover
      isOpen={isOpen}
      onClose={close}
      placement="bottom-end"
      isLazy
    >
      <PopoverTrigger>
        {variant === "pill" ? (
          <CategoryPill
            as="button"
            type="button"
            movement={movement}
            iconOnly={iconOnly}
            maxW={maxW}
            _hover={{ opacity: 0.85 }}
            onClick={toggle}
          />
        ) : (
        <Tag
          size="sm"
          variant="subtle"
          fontSize={size}
          textTransform="uppercase"
          fontWeight="600"
          borderRadius="full"
          px={size === "2xs" ? "6px" : 1}
          py="2px"
          letterSpacing="0.03em"
          whiteSpace="nowrap"
          cursor="pointer"
          maxW={maxW}
          _hover={{ opacity: 0.8 }}
          onClick={toggle}
          {...getCategoryTagProps(movement)}
        >
          {/* fontSize="inherit": Chakra's Text carries its own default size
              and would otherwise override the Tag's. A category with no icon
              of its own keeps its name — an empty chip identifies nothing. */}
          <Text as="span" fontSize="inherit" isTruncated>
            {iconOnly && movement.category?.icon
              ? movement.category.icon
              : getCategoryDisplay(movement)}
          </Text>
        </Tag>
        )}
      </PopoverTrigger>
      <PopoverContent
        ref={popoverRef}
        w="250px"
        boxShadow="0 4px 24px rgba(0,0,0,0.12)"
        borderRadius="12px"
        border="1px solid"
        borderColor="redesign.border"
        zIndex={10}
        onClick={(e) => e.stopPropagation()}
        overflow="hidden"
      >
        <PopoverBody p={0}>
          <Flex align="center" gap={2} px={4} py="8px" borderBottom="1px solid" borderBottomColor="redesign.border">
            <SearchIcon color="redesign.text.muted" boxSize="12px" />
            <Input
              placeholder={t('shared.categoryPicker.searchPlaceholder')}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              variant="unstyled"
              fontSize="sm"
              h="28px"
              autoFocus
            />
          </Flex>
          <Box maxH="240px" overflowY="auto" py={2}>
            {filteredCategories.map(cat => {
              const isCurrent = cat.id === movement.category?.id;
              return (
                <Flex
                  key={cat.id}
                  align="center"
                  gap={3}
                  px={4}
                  py="10px"
                  cursor="pointer"
                  _hover={{ bg: "redesign.bg.hover" }}
                  bg={isCurrent ? "redesign.bg.accent" : "transparent"}
                  color="redesign.text"
                  fontWeight={isCurrent ? "600" : "400"}
                  onClick={() => handleSelect(cat)}
                  fontSize="sm"
                >
                  <Text fontSize="md">{cat.icon || ''}</Text>
                  <Text>{cat.name}</Text>
                </Flex>
              );
            })}
            {filteredCategories.length === 0 && (
              <Text px={4} py={3} fontSize="sm" color="redesign.text.muted">{t('shared.categoryPicker.noResults')}</Text>
            )}
          </Box>
        </PopoverBody>
      </PopoverContent>
    </Popover>
  );
};

export default InlineCategoryPicker;
